import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withStyles, Typography } from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Select from '@material-ui/core/Select';
import moment from 'moment';
import DirectDebitRenewalPaymentForm from './DirectDebitRenewalPaymentForm';
import VoucherForm from './VoucherForm';
import ContentHeader from './ContentHeader';
import {
    formatTicketType,
    formatDate,
    formatPrice
} from '../helpers/formatters';
import { getCurrencySymbol, getYearGroups } from '../helpers/ticket';
import ZonesForm from './ZonesForm';
import globalStyles from '../styles';

const styles = {
    root: {
        textAlign: 'left',
        background: '#fff',
        boxShadow: '0px 2px 5px 0px rgba(0,0,0,0.21)'
    },
    input: {
        width: '100%',
        maxWidth: '250px',
        display: 'inline-block',
        margin: '0px auto 0px',
        verticalAlign: 'unset',
        '& > div': {
            display: 'block'
        },
        '& input': {
            maxWidth: '220px'
        }
    },
    description: {
        padding: '10px',
        marginBottom: '20px',
        fontWeight: '100'
    },
    zonesDescription: {
        padding: '20px 0 0',
        fontWeight: '100',
        textAlign: 'center'
    },
    button: {
        height: '40px',
        marginTop: '10px',
        marginBottom: '10px'
    },
    passengerInfo: {
        padding: '10px',
        marginBottom: '20px',
        textAlign: 'center'
    },
    formControl: {
        maxWidth: 250,
        width: '100%',
        margin: '0 auto',
        display: 'flex'
    },
    zoneMenuItem: {
        whiteSpace: 'normal'
    },
    paymentHelp: {
        ...globalStyles.link,
        padding: '1em 0',
        display: 'block'
    },
    zonesForm: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        paddingBottom: '35px'
    },
    link: globalStyles.link
};

class ConfirmDirectDebitRenewal extends Component {
    constructor(props) {
        super(props);

        this.state = {
            voucher: null,
            stop: props.stop,
            yr: props.yr,
            dest: props.dest,
            zone: props.zone,
            zoneStop: props.zoneStop,
            extId: props.extId
        };
    }

    setVoucher = voucher => {
        this.setState({ voucher });
    };

    calcMonthlyPriceAfterDiscount = (ticket, priceMonthly) => {
        const { voucher } = this.state;

        if (voucher) {
            const monthlyDiscount =
                (Number(voucher.value) * 100) /
                Number(ticket.num_monthly_payments);
            return (Number(priceMonthly) * 100 - monthlyDiscount) / 100;
        }

        return Number(priceMonthly);
    };

    onBusStopChange = event => {
        this.setState({
            stop: event.target.value
        });
    };

    onDestinationChange = event => {
        this.setState({
            dest: event.target.value
        });
    };

    getVoucherMessage = (
        ticket,
        total,
        roundedMonthlyPricePenceAfterDiscount,
        priceMonthly,
        priceDeposit
    ) => {
        const hasFullValueVoucher = roundedMonthlyPricePenceAfterDiscount < 100;
        const hasMonthlyPayments = parseFloat(priceMonthly) > 0;
        const { voucher } = this.state;

        if (!hasMonthlyPayments) {
            return '';
        }

        if (!voucher) {
            return '';
        }

        if (hasFullValueVoucher) {
            return (
                <>
                    <Typography variant="body2" style={{ color: 'red' }}>
                        Full value voucher codes cannot be used with this type
                        of ticket.
                    </Typography>
                    <Typography variant="body2" style={{ color: 'red' }}>
                        Please contact your transport operator for an
                        alternative ticket to use your voucher on.
                    </Typography>
                </>
            );
        }

        return (
            <>
                <Typography variant="body2">
                    Monthly payments reduced to{' '}
                    {getCurrencySymbol(ticket.currency)}
                    {(roundedMonthlyPricePenceAfterDiscount / 100).toFixed(2)}.
                </Typography>
                <Typography variant="body2">
                    New total: {getCurrencySymbol(ticket.currency)}
                    {priceDeposit} + {getCurrencySymbol(ticket.currency)}
                    {(roundedMonthlyPricePenceAfterDiscount / 100).toFixed(2)}
                    {' x '}
                    {ticket.num_monthly_payments} ={' '}
                    {getCurrencySymbol(ticket.currency)}
                    {total.toFixed(2)}.
                </Typography>
            </>
        );
    };

    onYearGroupChange = event => {
        this.setState({
            yr: event.target.value
        });
    };

    onExtIdChange = event => {
        this.setState({
            extId: event.target.value
        });
    };

    render() {
        const {
            passenger,
            ticket,
            client,
            service,
            renewal,
            classes,
            requireStudentId
        } = this.props;

        const { voucher, stop, yr, dest, zone, zoneStop, extId } = this.state;

        const useZones =
            service.zones &&
            ticket.pricing_model === 'Zonal' &&
            ticket.zone_prices;

        const priceDeposit = useZones
            ? ticket.zone_prices_deposit[zone]
            : ticket.price_deposit;

        const priceMonthly = useZones
            ? ticket.zone_prices_monthly[zone]
            : ticket.price_monthly;

        const monthlyPriceAfterDiscount = this.calcMonthlyPriceAfterDiscount(
            ticket,
            priceMonthly
        );

        const roundedMonthlyPricePenceAfterDiscount = Math.round(
            monthlyPriceAfterDiscount * 100
        );

        const totalMonthlyPriceAfterDiscount =
            (roundedMonthlyPricePenceAfterDiscount / 100).toFixed(2) *
            ticket.num_monthly_payments;

        const total =
            Number(totalMonthlyPriceAfterDiscount) + Number(priceDeposit || 0);

        const voucherMessage = this.getVoucherMessage(
            ticket,
            total,
            roundedMonthlyPricePenceAfterDiscount,
            priceMonthly,
            priceDeposit
        );

        const hasFullValueVoucher =
            parseFloat(priceMonthly) > 0 &&
            roundedMonthlyPricePenceAfterDiscount < 100;

        let yearGroups = [];

        if (service) {
            yearGroups = getYearGroups(service.yr_groups);
        }

        const showDestination =
            service && service.destinations && service.destinations.length > 0;

        return (
            <div>
                <div className={classes.root}>
                    <ContentHeader
                        text="Renew ticket"
                        id="ticket-details-heading"
                    />
                    <div
                        className={classes.passengerInfo}
                        style={{ marginBottom: 0 }}
                    >
                        <Typography variant="body2">
                            Passenger: {passenger.first_name}{' '}
                            {passenger.last_name}
                        </Typography>
                        <Typography variant="body2">
                            Ticket:{' '}
                            {formatTicketType(
                                service.name,
                                ticket.type,
                                ticket.sub_type,
                                ticket.travel_pattern
                            )}{' '}
                            {ticket.year}
                        </Typography>
                        <Typography variant="body2">
                            Valid from: {formatDate(ticket.start)}
                        </Typography>
                        <Typography variant="body2">
                            Valid until: {formatDate(ticket.expiry)}
                        </Typography>
                        {parseFloat(priceMonthly) <= 0 && (
                            <>
                                <Typography variant="body2">
                                    Total price:{' '}
                                    {formatPrice(total, ticket.currency)}
                                </Typography>
                            </>
                        )}
                        {parseFloat(priceMonthly) > 0 && (
                            <div>
                                <h3>Pricing</h3>
                                <Typography variant="body2">
                                    Initial payment due now:{' '}
                                    {formatPrice(priceDeposit, ticket.currency)}
                                </Typography>
                                <Typography variant="body2">
                                    Monthly payments:{' '}
                                    {formatPrice(
                                        monthlyPriceAfterDiscount,
                                        ticket.currency
                                    )}
                                    {' x '}
                                    {ticket.num_monthly_payments}
                                    {voucher && voucher.value && (
                                        <> (discounted)</>
                                    )}
                                </Typography>
                                <Typography variant="body2">
                                    First monthly payment:{' 1st '}
                                    {moment(ticket.monthly_sub_start).format(
                                        'MMMM YYYY'
                                    )}
                                </Typography>
                                <Typography variant="body2">
                                    Total price:{' '}
                                    {formatPrice(total, ticket.currency)}
                                </Typography>
                                <p>
                                    <a
                                        className={classes.paymentHelp}
                                        href="https://help.shuttleid.uk/en/articles/7852307-how-payments-work-with-direct-debit"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        How Direct Debit payments work
                                    </a>
                                </p>
                            </div>
                        )}
                    </div>
                    <div
                        style={{
                            background: '#fdfdcf',
                            // maxWidth: 600,
                            margin: '0 auto 20px',
                            padding: 10,
                            textAlign: 'center'
                        }}
                    >
                        <Typography variant="body2">
                            Need a different ticket type? Contact {client.name}{' '}
                            to discuss your renewal preference.
                        </Typography>
                    </div>
                </div>
                {requireStudentId && (
                    <div className={classes.root} style={{ marginBottom: 20 }}>
                        <ContentHeader
                            text="Student ID"
                            id="student-id-heading"
                        />
                        <div style={{ padding: '35px 0' }}>
                            <FormControl
                                variant="outlined"
                                className={classes.formControl}
                            >
                                <TextField
                                    autoComplete="off"
                                    className={`${classes.input} input`}
                                    id="id"
                                    name="id"
                                    type="text"
                                    label="Student ID"
                                    value={extId}
                                    onChange={this.onExtIdChange}
                                    variant="outlined"
                                />
                            </FormControl>
                            <Typography
                                className={classes.zonesDescription}
                                variant="body2"
                            >
                                <a
                                    href="https://help.shuttleid.uk/en/articles/7949374-how-to-find-my-student-id"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={classes.link}
                                >
                                    Help finding your student id
                                </a>
                            </Typography>
                        </div>
                    </div>
                )}
                {useZones && (
                    <div className={classes.root} style={{ marginBottom: 30 }}>
                        <ContentHeader
                            text="Zone selection"
                            id="availability-heading"
                        />
                        <Typography
                            className={classes.zonesDescription}
                            variant="body2"
                        >
                            Select your travel zone.{' '}
                            {service.timetable_url && (
                                <>
                                    <a
                                        href={service.timetable_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={classes.link}
                                    >
                                        Help finding your zone
                                    </a>
                                    .
                                </>
                            )}
                        </Typography>
                        <div className={classes.zonesForm}>
                            <ZonesForm
                                classes={classes}
                                passenger={passenger}
                                service={service}
                                ticket={ticket}
                                handleChange={e => {
                                    this.setState({
                                        [e.target.name]: e.target.value
                                    });
                                }}
                                zone={zone}
                                zoneStop={zoneStop}
                            />
                        </div>
                    </div>
                )}
                {service && 'stops' in service && !useZones && (
                    <div className={classes.root} style={{ marginBottom: 20 }}>
                        <ContentHeader text="Bus stop" id="stop-heading" />
                        <div style={{ padding: '35px 0' }}>
                            <FormControl
                                variant="outlined"
                                className={classes.formControl}
                            >
                                <InputLabel style={{ background: 'white' }}>
                                    Bus Stop
                                </InputLabel>
                                <Select
                                    id="stop"
                                    name="stop"
                                    value={stop}
                                    onChange={this.onBusStopChange}
                                    variant="outlined"
                                    input={
                                        <OutlinedInput
                                            name="stop"
                                            labelWidth={0}
                                            id="outlined-stop"
                                        />
                                    }
                                >
                                    {service.stops.map(serviceStop => (
                                        <MenuItem
                                            key={`stop-${serviceStop}`}
                                            value={serviceStop}
                                        >
                                            {serviceStop}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                    </div>
                )}

                {showDestination && (
                    <div className={classes.root} style={{ marginBottom: 20 }}>
                        <ContentHeader text="Destination" id="dest-heading" />
                        <div style={{ padding: '35px 0' }}>
                            <FormControl
                                variant="outlined"
                                className={classes.formControl}
                            >
                                <InputLabel style={{ background: 'white' }}>
                                    Destination
                                </InputLabel>
                                <Select
                                    id="dest"
                                    name="dest"
                                    value={dest}
                                    onChange={this.onDestinationChange}
                                    variant="outlined"
                                    input={
                                        <OutlinedInput
                                            name="dest"
                                            labelWidth={0}
                                            id="outlined-dest"
                                        />
                                    }
                                >
                                    {service.destinations.map(serviceDest => (
                                        <MenuItem
                                            key={`dest-${serviceDest}`}
                                            value={serviceDest}
                                        >
                                            {serviceDest}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                    </div>
                )}

                {yearGroups.length > 0 && (
                    <div className={classes.root} style={{ marginBottom: 20 }}>
                        <ContentHeader text="Year group" id="yr-heading" />
                        <div style={{ padding: '35px 0' }}>
                            <FormControl
                                variant="outlined"
                                className={classes.formControl}
                            >
                                <InputLabel style={{ background: 'white' }}>
                                    Year group
                                </InputLabel>
                                <Select
                                    id="yr"
                                    name="yr"
                                    value={yr}
                                    onChange={this.onYearGroupChange}
                                    variant="outlined"
                                    input={
                                        <OutlinedInput
                                            name="yr"
                                            labelWidth={0}
                                            id="outlined-yr"
                                        />
                                    }
                                >
                                    {yearGroups.map(year => (
                                        <MenuItem
                                            key={`year-${year}`}
                                            value={year}
                                        >
                                            {year === 'reception'
                                                ? 'Reception'
                                                : `Year ${year}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                    </div>
                )}

                {parseFloat(priceMonthly) > 0 && (
                    <div className={classes.root}>
                        <ContentHeader text="Voucher" id="voucher-heading" />
                        <VoucherForm
                            client={client}
                            setVoucher={this.setVoucher}
                            message={voucherMessage}
                        />
                    </div>
                )}

                {!hasFullValueVoucher && (
                    <div className={classes.root}>
                        <ContentHeader
                            text="Pay by Direct Debit"
                            id="pay-heading"
                        />
                        <DirectDebitRenewalPaymentForm
                            ticket={ticket}
                            service={service}
                            passenger={passenger}
                            client={client}
                            voucher={voucher}
                            renewal={renewal}
                            stop={stop}
                            dest={dest}
                            zone={zone}
                            zoneStop={zoneStop}
                            yr={yr}
                            extId={extId}
                            requireStudentId={requireStudentId}
                        />
                    </div>
                )}
            </div>
        );
    }
}

ConfirmDirectDebitRenewal.propTypes = {
    passenger: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string
    }).isRequired,
    ticket: PropTypes.shape({
        start: PropTypes.string,
        expiry: PropTypes.string,
        price: PropTypes.string
    }).isRequired,
    client: PropTypes.shape().isRequired,
    service: PropTypes.shape().isRequired,
    classes: PropTypes.shape().isRequired,
    requireStudentId: PropTypes.bool
};

ConfirmDirectDebitRenewal.defaultProps = {
    requireStudentId: false
};

export default withStyles(styles)(ConfirmDirectDebitRenewal);
